import React from 'react'

const Features: React.FC = () => {
  return (
    <div id="features" className="py-16 sm:py-20 lg:py-28">
      {/* Racing stripe divider */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="h-[2px] w-full bg-gradient-to-r from-rose-600 via-rose-600/40 to-transparent" />
      </div>

      {/* Header: left-aligned with step counter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-14">
        <div className="flex items-end justify-between">
          <div>
            <span className="font-mono text-xs text-rose-500 uppercase tracking-widest mb-3 block">How It Works</span>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white text-left"
              style={{ letterSpacing: '-0.03em' }}
            >
              Three steps to your<br />
              <span className="text-gradient">dream BMW.</span>
            </h2>
          </div>
          <div className="hidden lg:flex items-center gap-3 font-mono text-sm text-neutral-600 pb-2">
            <span className="text-rose-500">01</span>
            <span className="text-neutral-700">—</span>
            <span>02</span>
            <span className="text-neutral-700">—</span>
            <span>03</span>
          </div>
        </div>
      </div>

      {/* 3 feature cards — equal columns */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

          {/* Card 1: Scan — Radar Sweep */}
          <div className="group relative bg-[#141414] rounded-xl border border-white/10 overflow-hidden hover:border-rose-600/30 transition-all duration-300">
            {/* Watermark number */}
            <div className="absolute top-4 right-4 font-mono text-[120px] font-bold text-white/[0.03] leading-none select-none pointer-events-none">
              01
            </div>

            {/* Visualization: radar sweep */}
            <div className="relative h-48 flex items-center justify-center overflow-hidden">
              <svg viewBox="0 0 200 200" className="w-40 h-40">
                {/* Radar rings */}
                <circle cx="100" cy="100" r="30" fill="none" stroke="rgba(225,29,72,0.1)" strokeWidth="1" />
                <circle cx="100" cy="100" r="55" fill="none" stroke="rgba(225,29,72,0.08)" strokeWidth="1" />
                <circle cx="100" cy="100" r="80" fill="none" stroke="rgba(225,29,72,0.05)" strokeWidth="1" />
                {/* Center dot */}
                <circle cx="100" cy="100" r="3" fill="#e11d48" />
                {/* Static scan line */}
                <line x1="100" y1="100" x2="100" y2="20" stroke="#e11d48" strokeWidth="1.5" opacity="0.6" transform="rotate(45 100 100)" />
                {/* Static sweep */}
                <path d="M100,100 L100,20 A80,80 0 0,1 156,40 Z" fill="rgba(225,29,72,0.08)" transform="rotate(45 100 100)" />
                {/* Static dots */}
                <circle cx="130" cy="65" r="2.5" fill="#e11d48" opacity="0.7" />
                <circle cx="70" cy="130" r="2" fill="#e11d48" opacity="0.4" />
                <circle cx="145" cy="115" r="2" fill="#e11d48" opacity="0.5" />
              </svg>
            </div>

            {/* Text area */}
            <div className="px-6 pb-6">
              <span className="font-mono text-[10px] text-rose-500 uppercase tracking-widest mb-2 block">Step 01</span>
              <h3 className="text-2xl font-bold text-white mb-2">We Scan Every Dealer</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">
                BMWFinder continuously scans nationwide dealer inventory across E, F, and G chassis to find special options most search tools miss.
              </p>
            </div>
          </div>

          {/* Card 2: VIN Decode */}
          <div className="group relative bg-[#141414] rounded-xl border border-white/10 overflow-hidden hover:border-rose-600/30 transition-all duration-300">
            {/* Watermark number */}
            <div className="absolute top-4 right-4 font-mono text-[120px] font-bold text-white/[0.03] leading-none select-none pointer-events-none">
              02
            </div>

            {/* Visualization: VIN string with highlights */}
            <div className="relative h-48 flex flex-col items-center justify-center overflow-hidden px-4">
              <div className="font-mono text-sm sm:text-base text-neutral-600 tracking-widest mb-4 select-none">
                <span className="text-rose-500 bg-rose-600/10 px-1 rounded">WBA</span>
                <span className="text-white bg-white/5 px-1 rounded">S8</span>
                <span className="text-neutral-500">C5</span>
                <span className="text-rose-400 bg-rose-600/10 px-1 rounded">5</span>
                <span className="text-neutral-500">J</span>
                <span className="text-white bg-white/5 px-1 rounded">A40</span>
                <span className="text-neutral-500">9128</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-1.5">
                {['BMW AG', 'M3 Comp', 'G80', '2024', 'Individual'].map((badge) => (
                  <span
                    key={badge}
                    className="px-2 py-1 rounded bg-white/5 border border-white/10 font-mono text-[10px] text-neutral-400"
                  >
                    {badge}
                  </span>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-mono text-[10px] text-emerald-500 uppercase tracking-wider">Verified</span>
              </div>
            </div>

            {/* Text area */}
            <div className="px-6 pb-6">
              <span className="font-mono text-[10px] text-rose-500 uppercase tracking-widest mb-2 block">Step 02</span>
              <h3 className="text-2xl font-bold text-white mb-2">VIN Decoded & Verified</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">
                Every car gets its VIN decoded through BMW's official decoder. We verify individual colors, bucket seats, and competition packages.
              </p>
            </div>
          </div>

          {/* Card 3: Filter — Mini mock filter UI */}
          <div className="group relative bg-[#141414] rounded-xl border border-white/10 overflow-hidden hover:border-rose-600/30 transition-all duration-300">
            {/* Watermark number */}
            <div className="absolute top-4 right-4 font-mono text-[120px] font-bold text-white/[0.03] leading-none select-none pointer-events-none">
              03
            </div>

            {/* Visualization: mini filter UI */}
            <div className="relative h-48 flex flex-col items-center justify-center overflow-hidden px-5">
              <div className="w-full max-w-[220px] space-y-2">
                {/* Mini dropdown rows */}
                {[
                  { label: 'Chassis', value: 'G80, G82' },
                  { label: 'Color', value: 'Individual' },
                  { label: 'Sort', value: 'Price: Low' },
                ].map((filter) => (
                  <div
                    key={filter.label}
                    className="flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                  >
                    <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-wider">{filter.label}</span>
                    <span className="text-xs text-white font-medium">{filter.value}</span>
                  </div>
                ))}
                {/* Result count */}
                <div className="flex items-center justify-center gap-2 pt-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                  <span className="font-mono text-xs text-rose-400">47 results</span>
                </div>
              </div>
            </div>

            {/* Text area */}
            <div className="px-6 pb-6">
              <span className="font-mono text-[10px] text-rose-500 uppercase tracking-widest mb-2 block">Step 03</span>
              <h3 className="text-2xl font-bold text-white mb-2">Filter & Find</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">
                Filter by chassis, color, price, transmission, and more. Sort by distance from your zip code and zero in on exactly what you want.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Spec-sheet strip */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#141414] rounded-xl border border-white/10 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
            {/* Distance Sorting */}
            <div className="flex items-start gap-4 p-6">
              <div className="flex-shrink-0 text-rose-500 mt-0.5">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-1">Distance Sorting</h4>
                <p className="text-xs text-neutral-500 leading-relaxed">Enter your zip code and sort by distance to find the closest match.</p>
              </div>
            </div>

            {/* Advanced Filters */}
            <div className="flex items-start gap-4 p-6">
              <div className="flex-shrink-0 text-rose-500 mt-0.5">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                  <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                  <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
                  <line x1="17" y1="16" x2="23" y2="16" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-1">Advanced Filters</h4>
                <p className="text-xs text-neutral-500 leading-relaxed">Price, transmission, year, chassis, color — zero in on what you want.</p>
              </div>
            </div>

            {/* Real-Time Updates */}
            <div className="flex items-start gap-4 p-6">
              <div className="flex-shrink-0 text-rose-500 mt-0.5">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-1">Real-Time Updates</h4>
                <p className="text-xs text-neutral-500 leading-relaxed">Cars stream into your browser live. Be the first to spot new listings.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Features
